import moment from 'moment';

import { ApiResponseObject } from '@api/v4/ApiResponseTypes';
import {
  AssetData,
  AssetResponse,
  AssetTypes,
  ColorData,
  ExternalMediumData,
  FontData,
  GenericFileData,
  isExpectedDataShape,
  LinkObject,
  PersonData,
  PressData,
  Tag
} from '@api/v4/assets/assetTypes';
import { CustomFieldMapsResponse, CustomFieldMapsResponseObject } from '@api/v4/assets/customFieldTypes';
import { Attachment } from '@api/v4/attachments/attachmentTypes';
import {
  ActionTypes,
  AssetDetailsPayload,
  CreateCustomFieldPayloadOperations,
  FlatCustomFieldMaps,
  FlattenedAttachment,
  InitialModalEditFormContentState,
  isAssetDetailsAction,
  isAttachmentDetailsAction,
  isCreateCustomFieldMapsAction,
  isDeleteAttachmentAction,
  isFetchCustomFieldKeysAction,
  isFetchCustomFieldMapsAction,
  isInitialAction,
  isNewAssetAction,
  isReplaceAttachmentAction,
  isRepositionAttachmentAction,
  isResolveAssetAction,
  isResolveAttachmentUpdateAction,
  isResolveDeletedAttachmentAction,
  isResolveDeletedCustomFieldValueAction,
  isResolveNewCustomFieldsAction,
  isResolveUpdatedCustomFieldAction,
  isThumbnailOverrideAction,
  isUpdateCustomFieldMapsAction,
  isUploadNewAttachment,
  isValidationErrorAction,
  isWatermarkAction,
  ModalEditFormContentState,
  ModalEditFormReducerAction,
  ReducerState,
  ThumbnailOverridePayloadOperations,
  ValidationErrors,
  WatermarkPayloadOperations,
  isUpdateLocaleAction,
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { isValidHex, newAssetInitialState, newAssetEmptyEditState } from './helpers';

const getFirstAttachmentKey = (attachments: FlattenedAttachment[]): string => {
  const posZeroAttachments = attachments.filter((attachment) => attachment.position === 0);
  // either we have one attachment with position zero and we want to get that one
  // or we have many attachments with position zero and we want to default to the first one
  return posZeroAttachments[0] ? posZeroAttachments[0].key : '';
};

const getIncludedResources = <T>(included: ApiResponseObject<T>[], type: string): ApiResponseObject<T>[] => (
  included.filter((item) => item.type === type)
);

const validateResponse = (response: AssetResponse): void => {
  if (!response.data) {
    throw new Error('response does not have data');
  }
};

const flattenAttachments = (attachments: ApiResponseObject<Attachment>[]): FlattenedAttachment[] => (
  attachments.map(({ id, attributes }) => ({
    extension: attributes.extension,
    filename: attributes.filename,
    key: id,
    mimetype: attributes.mimetype,
    size: attributes.size,
    temporaryAttachment: false,
    url: attributes.url,
    height: attributes.height,
    position: attributes.position,
    thumbnailUrl: attributes.thumbnail_url,
    width: attributes.width
  }))
);

const getResponseDetails = (response: AssetResponse): Partial<InitialModalEditFormContentState> => {
  validateResponse(response);
  // eslint-disable-next-line no-param-reassign
  response = response.included ? response : { ...response, included: [] };

  return {
    asset: {
      ...response.data.attributes,
      description: response.data.attributes.description?.replace(/\r/g, "\n"), // remove carriage return (\r) for trumbowyg
    },
    assetKey: response.data.id,
    assetType: response.data.type as AssetTypes,
    attachments: flattenAttachments(getIncludedResources<Attachment>(response.included, 'attachments')),
    customFieldMaps: null,
    customFieldKeys: null,
    tags: getIncludedResources<Tag>(response.included, 'tags').map(({ attributes }) => attributes),
  };
};

const transformAssetResponse = (
  {
    asset,
    assetType,
    tags,
    attachments,
  }: Partial<InitialModalEditFormContentState>
): Partial<ModalEditFormContentState> => {
  const getExpectedData = <T extends AssetData>(expectedAssetType: AssetTypes): T | null => (
    isExpectedDataShape<T>(assetType, expectedAssetType, asset.data) ? asset.data : null
  );

  const rawPressData = getExpectedData<PressData>(AssetTypes.Press);
  let pressData = rawPressData;
  const formattedPublishDate = rawPressData?.published_date ? moment(rawPressData.published_date).format('MM/DD/YYYY') : undefined;
  if (formattedPublishDate) {
    pressData = {
      ...pressData,
      published_date: formattedPublishDate
    };
  }
  return {
    assetDescription: asset.description || '',
    assetName: asset.name,
    attachments: attachments.sort((attachmentA, attachmentB) => attachmentA.position - attachmentB.position),
    colorData: getExpectedData<ColorData>(AssetTypes.Color),
    externalMediumData: getExpectedData<ExternalMediumData>(AssetTypes.ExternalMedium),
    fontData: getExpectedData<FontData>(AssetTypes.Font),
    firstAttachmentKey: getFirstAttachmentKey(attachments),
    genericFileData: getExpectedData<GenericFileData>(AssetTypes.GenericFile),
    personData: getExpectedData<PersonData>(AssetTypes.People),
    pressData,
    tags: tags.map((attributes) => ({ ...attributes, id: attributes.name })),
    thumbnailUrl: asset.thumbnail_url,
    thumbnailOverride: asset.thumbnail_override,
    watermark: asset.watermark ? asset.watermark : {}
  };
};

const flattenCustomFieldMaps = (response: CustomFieldMapsResponse): FlatCustomFieldMaps[] => (
  response.data.map((field: CustomFieldMapsResponseObject) => (
    {
      key: {
        id: field.key.data.id,
        name: field.key.data.attributes.name,
        prioritized: field.key.data.attributes.prioritized,
        allowedValues: field.key.data.attributes.allowed_values,
        restricted: field.key.data.attributes.restricted
      },
      value: {
        id: field.value.data.id,
        value: field.value.data.attributes.value
      }
    }
  ))
);

const handleErrorStateUpdates = (state: ReducerState, payload: AssetDetailsPayload): ValidationErrors[] => {
  const newErrorState = [...state.errorState];
  if (payload.assetName && state.errorState.includes(ValidationErrors.MissingAssetName)) {
    newErrorState.splice(newErrorState.indexOf(ValidationErrors.MissingAssetName), 1);
  }

  if (payload.assetDescription && state.errorState.includes(ValidationErrors.MissingDescription)) {
    newErrorState.splice(newErrorState.indexOf(ValidationErrors.MissingDescription), 1);
  }

  if (payload.externalMediumData
    && payload.externalMediumData.url
    && state.errorState.includes(ValidationErrors.MissingMediaUrl)
  ) {
    newErrorState.splice(newErrorState.indexOf(ValidationErrors.MissingMediaUrl), 1);
  }

  if (payload.fontData
    && (payload.fontData.other_font_id || payload.fontData.web_font_id)
  ) {
    if (state.errorState.includes(ValidationErrors.MissingFontInput)) {
      newErrorState.splice(newErrorState.indexOf(ValidationErrors.MissingFontInput));
    }
  }

  if (payload.colorData) {
    if (state.errorState.includes(ValidationErrors.InvalidHexValue)) {
      if (payload.colorData.hex) {
        if (isValidHex(payload.colorData.hex)) {
          newErrorState.splice(newErrorState.indexOf(ValidationErrors.InvalidHexValue), 1);
        }
      } else if (payload.colorData.hex === '') {
        newErrorState.splice(newErrorState.indexOf(ValidationErrors.InvalidHexValue), 1);
      }
    }
  }

  return newErrorState;
};

/* helper for updating positions after deleting attachment, note that it mutates attachmentsCopy */
const handleDeleteAttachment = (attachmentsCopy: FlattenedAttachment[], index: number): { thumbnailUrl: string } | false => {
  attachmentsCopy.splice(index, 1);
  for (let i = 0; i < attachmentsCopy.length; i += 1) {
    // eslint-disable-next-line no-param-reassign
    attachmentsCopy[i].position = i;
  }
  // update asset thumbnail image if first attachment is deleted
  const updateAssetThumbnail = index === 0;
  const thumbnailUrl = attachmentsCopy[0]?.thumbnailUrl || attachmentsCopy[0]?.url || '';
  return updateAssetThumbnail && { thumbnailUrl };
};

const modalEditFormReducer = (
  state: ReducerState,
  action: ModalEditFormReducerAction
): ReducerState => {
  if (isInitialAction(action)) {
    const { printui, response } = action.payload;
    const responseDetails = getResponseDetails(response);
    return {
      ...state,
      initialData: { ...responseDetails, printui } as InitialModalEditFormContentState,
      editState: {
        ...transformAssetResponse(responseDetails),
        tagSearchSuggestions: null,
        customFieldMaps: null,
        createCustomFieldMaps: [],
        terribleTrumbowygFlag: state.ugtLocale
      } as ModalEditFormContentState,
      errorState: []
    };
  }

  if (isNewAssetAction(action)) {
    const { assetType } = action.payload;
    return {
      ugtLocale: BFG.locales.ugtLocaleDefault,
      initialData: newAssetInitialState(assetType),
      editState: newAssetEmptyEditState(),
      errorState: []
    };
  }

  if (action.type === ActionTypes.ResolveTags) {
    return {
      ...state,
      initialData: {
        ...state.initialData,
        tags: state.editState.tags as Tag[]
      }
    };
  }

  if (isRepositionAttachmentAction(action)) {
    const { hoveredIndex, draggedIndex } = action.payload;
    const attachments = state.editState.attachments.map((attachment) => ({
      ...attachment
    }));
    const draggedItem = attachments[draggedIndex];

    attachments.splice(draggedIndex, 1); // remove dragged attachmemt from attachments
    attachments.splice(hoveredIndex, 0, draggedItem); // insert dragged attachment into hovered spot

    const updatedAttachments = attachments.map((attachment, i) => {
      const newAttachment = attachment;
      newAttachment.position = i;
      return newAttachment;
    });

    // update asset thumbnail image if first attachment is updated
    const updateAssetThumbnail = hoveredIndex === 0 || draggedIndex === 0;
    const attachmentThumbnailUrl = updatedAttachments[0]?.thumbnailUrl || updatedAttachments[0].url;

    return {
      ...state,
      editState: {
        ...state.editState,
        attachments: updatedAttachments,
        ...updateAssetThumbnail && { thumbnailUrl: attachmentThumbnailUrl }
      }
    };
  }

  if (isUploadNewAttachment(action)) {
    const isFirstAttachment = !state.editState.attachments.length;

    return {
      ...state,
      editState: {
        ...state.editState,
        attachments: [...state.editState.attachments, ...action.payload],
        ...isFirstAttachment && { thumbnailUrl: action.payload[0].url },
      }
    };
  }

  if (isAttachmentDetailsAction(action)) {
    const { index, filename } = action.payload;
    const attachmentsCopy = [...state.editState.attachments];
    attachmentsCopy[index] = {
      ...attachmentsCopy[index],
      filename
    };
    return {
      ...state,
      editState: {
        ...state.editState,
        attachments: attachmentsCopy,
      }
    };
  }

  if (isReplaceAttachmentAction(action)) {
    const { index, replacement } = action.payload;
    const replacementWithOriginalKey = {
      ...replacement,
      key: state.editState.attachments[index].key
    };
    const attachmentsCopy = [...state.editState.attachments];
    attachmentsCopy[index] = replacementWithOriginalKey;
    return {
      ...state,
      editState: {
        ...state.editState,
        attachments: attachmentsCopy,
        ...(index === 0) && { thumbnailUrl: attachmentsCopy[0]?.url || '' }
      }
    };
  }

  if (isDeleteAttachmentAction(action)) {
    const { index } = action.payload;
    const attachmentsCopy = [...state.editState.attachments];
    const hasThumbnailUpdate = handleDeleteAttachment(attachmentsCopy, index);
    return {
      ...state,
      editState: {
        ...state.editState,
        attachments: attachmentsCopy,
        ...hasThumbnailUpdate && { thumbnailUrl: hasThumbnailUpdate.thumbnailUrl }
      }
    };
  }

  if (isResolveDeletedAttachmentAction(action)) {
    const { attachmentKey } = action.payload;
    const attachmentsCopy = [...state.initialData.attachments];
    const index = attachmentsCopy.findIndex((attachment) => attachment.key === attachmentKey);
    const hasThumbnailUpdate = handleDeleteAttachment(attachmentsCopy, index);
    return {
      ...state,
      initialData: {
        ...state.initialData,
        attachments: attachmentsCopy,
        ...hasThumbnailUpdate && { thumbnailUrl: hasThumbnailUpdate.thumbnailUrl }
      }
    };
  }

  if (isResolveAttachmentUpdateAction(action)) {
    const { id, attributes } = action.payload;
    const attachmentsCopy = [...state.initialData.attachments];
    const targetAttachmentIndex = attachmentsCopy.findIndex((attachment) => attachment.key === id);
    attachmentsCopy[targetAttachmentIndex] = {
      ...attachmentsCopy[targetAttachmentIndex],
      filename: attributes.filename,
      url: attributes.url,
      extension: attributes.extension,
      position: attributes.position
    };
    return {
      ...state,
      initialData: {
        ...state.initialData,
        attachments: attachmentsCopy
      }
    };
  }

  if (isAssetDetailsAction(action)) {
    return {
      ...state,
      editState: {
        ...state.editState,
        ...action.payload,
      },
      errorState: handleErrorStateUpdates(state, action.payload)
    };
  }

  if (isResolveAssetAction(action)) {
    const { asset, newAttachmentKeys } = action.payload;
    let newAttachments = [];
    if (newAttachmentKeys) {
      newAttachments = newAttachmentKeys.map((key) => state.editState.attachments.find((attachment) => attachment.key === key));
    }
    return {
      ...state,
      initialData: {
        ...state.initialData,
        asset: {
          ...state.initialData.asset,
          ...asset,
          // We don't get data back from the asset response, so copy editState data if it exists
          // Note that the data fields (colorData, externalMediumData etc) on the editState are
          // exclusive to each other.
          data: state.editState.colorData
            || state.editState.externalMediumData
            || state.editState.fontData
            || state.editState.genericFileData
            || state.editState.personData
            || state.editState.pressData
            || undefined,
          thumbnail_override: state.editState.thumbnailOverride || undefined,
          watermark: state.editState.watermark
        },
        attachments: [
          ...state.initialData.attachments,
          ...newAttachments
        ]
      }
    };
  }

  if (isFetchCustomFieldKeysAction(action)) {
    return {
      ...state,
      initialData: {
        ...state.initialData,
        customFieldKeys: action.payload.response
      }
    };
  }

  // call new flattendCustomFieldMaps on each state assignment
  // so we aren't pointing to the same reference in memory
  if (isFetchCustomFieldMapsAction(action)) {
    return {
      ...state,
      initialData: {
        ...state.initialData,
        customFieldMaps: flattenCustomFieldMaps(action.payload.response)
      },
      editState: {
        ...state.editState,
        customFieldMaps: flattenCustomFieldMaps(action.payload.response)
      }
    };
  }

  if (isUpdateCustomFieldMapsAction(action)) {
    const { key, value } = action.payload;
    const copyCustomFieldMaps = [...state.editState.customFieldMaps];
    let customFieldMap = copyCustomFieldMaps.find((field) => key.id === field.key.id);
    // if value exists, copy it
    if (customFieldMap) {
      const currentIndex = copyCustomFieldMaps.indexOf(customFieldMap);
      customFieldMap = {
        key: {
          ...customFieldMap.key
        },
        value: {
          ...customFieldMap.value,
          value
        }
      };
      copyCustomFieldMaps[currentIndex] = customFieldMap;
    } else {
      // otherwise make a new one with a null value.id
      customFieldMap = {
        key: {
          id: key.id,
          name: key.attributes.name,
          prioritized: key.attributes.prioritized,
          allowedValues: key.attributes.allowed_values,
          restricted: key.attributes.restricted
        },
        value: {
          id: null,
          value
        }
      };
      copyCustomFieldMaps.push(customFieldMap);
    }

    return {
      ...state,
      editState: {
        ...state.editState,
        customFieldMaps: copyCustomFieldMaps
      }
    };
  }

  if (isResolveUpdatedCustomFieldAction(action)) {
    const { data } = action.payload;
    const copyCustomFieldMaps = [...state.initialData.customFieldMaps];
    const customFieldIndex = copyCustomFieldMaps.findIndex((customField) => customField.value.id === data.id);
    copyCustomFieldMaps[customFieldIndex].value = {
      id: data.id,
      value: data.attributes.value
    };
    return {
      ...state,
      initialData: {
        ...state.initialData,
        customFieldMaps: copyCustomFieldMaps
      }
    };
  }

  if (isCreateCustomFieldMapsAction(action)) {
    const { index = null, key = '', value = '', operation } = action.payload;
    const copyCreateCustomFieldMaps = [...state.editState.createCustomFieldMaps];

    switch (operation) {
      case CreateCustomFieldPayloadOperations.Create:
        copyCreateCustomFieldMaps.unshift({ key, value });
        break;
      case CreateCustomFieldPayloadOperations.Update:
        copyCreateCustomFieldMaps[index] = { key, value };
        break;
      case CreateCustomFieldPayloadOperations.Delete:
        copyCreateCustomFieldMaps.splice(index, 1);
        break;
      default:
        return state;
    }
    return {
      ...state,
      editState: {
        ...state.editState,
        createCustomFieldMaps: copyCreateCustomFieldMaps
      }
    };
  }

  if (isResolveNewCustomFieldsAction(action)) {
    const { data } = action.payload;
    const initialCustomFieldsCopy = [...state.initialData.customFieldMaps];
    const editCustomFieldsCopy = [...state.editState.customFieldMaps];
    const initialCustomFieldKeysCopy = [...state.initialData.customFieldKeys];
    data.forEach(({ id, attributes }) => {
      initialCustomFieldsCopy.push({
        key: {
          id,
          name: attributes.key,
          prioritized: false,
          allowedValues: [],
          restricted: false
        },
        value: {
          id,
          value: attributes.value
        }
      });

      initialCustomFieldKeysCopy.push({
        id,
        type: "custom_field_keys",
        attributes: {
          name: attributes.key,
          prioritized: false,
          allowed_values: [],
          restricted: false
        }
      });

      editCustomFieldsCopy.push({
        key: {
          id,
          name: attributes.key,
          prioritized: false,
          allowedValues: [],
          restricted: false
        },
        value: {
          id,
          value: attributes.value
        }
      });
    });

    return {
      ...state,
      editState: {
        ...state.editState,
        createCustomFieldMaps: [],
        customFieldMaps: editCustomFieldsCopy
      },
      initialData: {
        ...state.initialData,
        customFieldMaps: initialCustomFieldsCopy,
        customFieldKeys: initialCustomFieldKeysCopy
      }
    };
  }

  if (isResolveDeletedCustomFieldValueAction(action)) {
    const { customFieldValueKey } = action.payload;
    const copyInitialCustomFields = [...state.initialData.customFieldMaps];
    const targetCustomField = copyInitialCustomFields.find((customField) => (
      customField.value.id === customFieldValueKey
    ));
    targetCustomField.value.value = "";
    return {
      ...state,
      initialData: {
        ...state.initialData,
        customFieldMaps: copyInitialCustomFields
      }
    };
  }

  if (isValidationErrorAction(action)) {
    return {
      ...state,
      errorState: [...state.errorState, ...action.payload.validationErrors]
    };
  }

  if (isThumbnailOverrideAction(action)) {
    const { url = null, filename = null, operation } = action.payload;
    let copyThumbnailOverride = { ...state.editState.thumbnailOverride };
    switch (operation) {
      case ThumbnailOverridePayloadOperations.Update:
        copyThumbnailOverride = { url, filename };
        break;
      case ThumbnailOverridePayloadOperations.Delete:
        copyThumbnailOverride = {} as LinkObject;
        break;
      default:
        return state;
    }
    return {
      ...state,
      editState: {
        ...state.editState,
        thumbnailOverride: copyThumbnailOverride
      }
    };
  }

  if (isWatermarkAction(action)) {
    const { url = null, filename = null, operation } = action.payload;
    let copyWatermark = { ...state.editState.watermark };
    switch (operation) {
      case WatermarkPayloadOperations.Update:
        copyWatermark = { url, filename };
        break;
      case WatermarkPayloadOperations.Delete:
        copyWatermark = {} as LinkObject;
        break;
      default:
        return state;
    }
    return {
      ...state,
      editState: {
        ...state.editState,
        watermark: copyWatermark
      }
    };
  }

  if (isUpdateLocaleAction(action)) {
    const { ugtLocale } = action.payload;
    if (ugtLocale === state.ugtLocale) {
      return state;
    }

    return {
      ...state,
      ugtLocale
    };
  }

  return state;
};

export default modalEditFormReducer;
